import { defineStore } from "pinia";
import { computed, ref } from "vue";

/** sessionStorage key: one browser tab's break-glass, so a reload keeps it and a closed tab does not. */
export const BREAK_GLASS_STORAGE_KEY = "kiosk-backoffice.break-glass";
/** A break-glass entry restored on reload is honoured for one shift at most, however active the tab. */
export const BREAK_GLASS_MAX_AGE_MS = 8 * 60 * 60_000;

interface Persisted {
  casino: string;
  staffId: string;
  enteredAt: number;
}

function read(): Persisted | null {
  try {
    const raw = sessionStorage.getItem(BREAK_GLASS_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<Persisted>;
    return typeof p.casino === "string" &&
      typeof p.staffId === "string" &&
      typeof p.enteredAt === "number"
      ? (p as Persisted)
      : null;
  } catch {
    return null;
  }
}

function write(p: Persisted | null): void {
  try {
    if (p) sessionStorage.setItem(BREAK_GLASS_STORAGE_KEY, JSON.stringify(p));
    else sessionStorage.removeItem(BREAK_GLASS_STORAGE_KEY);
  } catch {
    /* storage unavailable: break-glass simply does not survive a reload */
  }
}

/**
 * Which Casino a `platform_admin` is currently acting at.
 *
 * **A UI mode, not a form field.** `platform_admin` passes every role check but must name a target
 * Casino on scoped routes and gets a `400` without one; scoped roles must not send one at all.
 * That is two different UIs, so the target lives in the shell for admins and does not exist for
 * anyone else, rather than appearing as an optional input on every form.
 *
 * Client state, so Pinia — it is a UI selection, not something the API told us. Nothing from the
 * API belongs in a store.
 *
 * **Survives a reload of the same tab, and nothing more.** It was first kept in memory only, so a
 * page reload threw the admin out of the Casino and back through the step-up every time — in
 * practice, after every refresh. It now lives in `sessionStorage`, which bounds it the way ADR-0005's
 * amendment needs: a closed tab ends it (a new tab never inherits it), it is restored only for the
 * same `staffId` that entered (`restore`, called once `/v1/me` has answered), it is dropped after
 * `BREAK_GLASS_MAX_AGE_MS`, and every way a session ends — logout, the central 401, another tab's
 * token change, the idle lock — goes through `useSession().clear()`, which calls `leave()` and
 * removes it. A reload is not a new entry, so it is not recorded again; the banner still shows it.
 */
export const useBreakGlass = defineStore("break-glass", () => {
  const casino = ref<string | null>(null);

  /**
   * **Step-up re-authentication belongs at the call site, before this runs** (EnterCasino.vue).
   * `staffId` — who stepped up — is what lets a reload restore it for that person only; without it
   * the mode is not persisted.
   */
  function enter(id: string, staffId?: string): void {
    casino.value = id;
    write(staffId ? { casino: id, staffId, enteredAt: Date.now() } : null);
  }

  function leave(): void {
    casino.value = null;
    write(null);
  }

  /**
   * After a reload: re-enter the Casino this tab was in, if the same admin entered it and it is not
   * older than one shift. Anything else is discarded.
   */
  function restore(staffId: string, now: number = Date.now()): void {
    const p = read();
    if (!p) return;
    if (p.staffId !== staffId || now - p.enteredAt >= BREAK_GLASS_MAX_AGE_MS) {
      write(null);
      return;
    }
    casino.value = p.casino;
  }

  return { casino, isActive: computed(() => casino.value !== null), enter, leave, restore };
});
